import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import { RootState } from '../../../stateStore'
import {
  AddInterviewerDetails,
  GetAllInterviewersDetails,
  CheckInterviewerAvailabilityProps,
  InterviewerConfigurationSliceState,
  InterviewerListAPIData,
  AddInterviewerDetailsResponse,
  DepartmentWiseEmployeesList,
  getJobCode,
  InterviewerListResponse,
} from '../../../types/Recruitment/InterviewerConfiguration/interviewerConfigurationTypes'
import interviewerConfigurationApi from '../../../middleware/api/Recruitment/IntervieweerConfiguration/interviewerConfigurationApi'

const getDepartmentWiseEmployeesList = createAsyncThunk(
  'interviewerConfiguration/getDepartmentWiseEmployeesList',
  async (departmentName: string, thunkApi) => {
    try {
      return await interviewerConfigurationApi.getDepartmentWiseEmployeesList(
        departmentName,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const addInterviewerDetails = createAsyncThunk(
  'interviewerConfiguration/addInterviewerDetails',
  async (data: AddInterviewerDetails, thunkApi) => {
    try {
      return await interviewerConfigurationApi.addInterviewerDetails(data)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const updateInterviewerDetails = createAsyncThunk(
  'interviewerConfiguration/updateInterviewerDetails',
  async (data: AddInterviewerDetails, thunkApi) => {
    try {
      return await interviewerConfigurationApi.updateInterviewerDetails(data)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getAllInterviewersList = createAsyncThunk(
  'interviewerList/getAllInterviewersDetails',
  async (data: GetAllInterviewersDetails, thunkApi) => {
    try {
      return await interviewerConfigurationApi.getAllInterviewersDetails(data)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getAllJobCodes = createAsyncThunk(
  'interviewerConfiguration/getAllJobCodes',
  async (_, thunkApi) => {
    try {
      return await interviewerConfigurationApi.getAllJobCodes()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const deleteInterviewerDetails = createAsyncThunk(
  'interviewerConfiguration/deleteInterviewerDetails',
  async (id: number, thunkApi) => {
    try {
      return await interviewerConfigurationApi.deleteInterviewerDetails(id)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const deleteCheckedInterviewerDetails = createAsyncThunk(
  'interviewerConfiguration/deleteCheckedInterviewerDetails',
  async (interviewerId: number[], thunkApi) => {
    try {
      return await interviewerConfigurationApi.deleteCheckedInterviewerDetails(
        interviewerId,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const isInterviewerAvailable = createAsyncThunk(
  'interviewerConfiguration/isInterviewerAvailable',
  async (data: CheckInterviewerAvailabilityProps, thunkApi) => {
    try {
      return await interviewerConfigurationApi.isInterviewerAvailable(data)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

export const initialInterviewerConfigurationState: InterviewerConfigurationSliceState =
  {
    isLoading: ApiLoadingState.idle,
    departmentWiseEmployeesList: [],
    getAllJobCodes: [],
    interviewerListResponse: [],
    listSize: 0,
    interviewerListAPIData: {} as InterviewerListAPIData,
    selectedInterviewerDetails: {} as AddInterviewerDetailsResponse,
  }

const interviewerConfigurationSlice = createSlice({
  name: 'interviewerConfiguration',
  initialState: initialInterviewerConfigurationState,
  reducers: {
    setSelectedInterviewerDetails: (state, action) => {
      state.selectedInterviewerDetails = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDepartmentWiseEmployeesList.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.departmentWiseEmployeesList = action.payload
      })
      .addCase(getAllJobCodes.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.getAllJobCodes = action.payload
      })
      .addCase(getAllInterviewersList.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.interviewerListResponse = action.payload.list
        state.listSize = action.payload.size
      })
      .addMatcher(
        isAnyOf(
          getDepartmentWiseEmployeesList.pending,
          addInterviewerDetails.pending,
          updateInterviewerDetails.pending,
          getAllJobCodes.pending,
          getAllInterviewersList.pending,
          deleteInterviewerDetails.pending,
          deleteCheckedInterviewerDetails.pending,
          isInterviewerAvailable.pending,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.loading
        },
      )
      .addMatcher(
        isAnyOf(
          getDepartmentWiseEmployeesList.rejected,
          addInterviewerDetails.rejected,
          updateInterviewerDetails.rejected,
          getAllJobCodes.rejected,
          getAllInterviewersList.rejected,
          deleteInterviewerDetails.rejected,
          deleteCheckedInterviewerDetails.rejected,
          isInterviewerAvailable.rejected,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.failed
        },
      )
      .addMatcher(
        isAnyOf(
          addInterviewerDetails.fulfilled,
          updateInterviewerDetails.fulfilled,
          deleteInterviewerDetails.fulfilled,
          deleteCheckedInterviewerDetails.fulfilled,
          isInterviewerAvailable.fulfilled,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.succeeded
        },
      )
  },
})
const isLoading = (state: RootState): LoadingState =>
  state.interviewerConfiguration.isLoading
const getSelectedInterviewer = (
  state: RootState,
): AddInterviewerDetailsResponse =>
  state.interviewerConfiguration.selectedInterviewerDetails
const departmentWiseEmployeesList = (
  state: RootState,
): DepartmentWiseEmployeesList[] =>
  state.interviewerConfiguration.departmentWiseEmployeesList

const jobCodes = (state: RootState): getJobCode[] =>
  state.interviewerConfiguration.getAllJobCodes

const interviewerResponseData = (state: RootState): InterviewerListResponse[] =>
  state.interviewerConfiguration.interviewerListResponse

const listSize = (state: RootState): number =>
  state.interviewerConfiguration.listSize

export const interviewerConfigurationThunk = {
  getDepartmentWiseEmployeesList,
  addInterviewerDetails,
  getAllJobCodes,
  getAllInterviewersList,
  deleteInterviewerDetails,
  updateInterviewerDetails,
  deleteCheckedInterviewerDetails,
  isInterviewerAvailable,
}

export const interviewerConfigurationSelectors = {
  isLoading,
  departmentWiseEmployeesList,
  jobCodes,
  interviewerResponseData,
  getSelectedInterviewer,
  listSize,
}

export const interviewerConfigurationService = {
  ...interviewerConfigurationThunk,
  actions: interviewerConfigurationSlice.actions,
  selectors: interviewerConfigurationSelectors,
}

export default interviewerConfigurationSlice.reducer
